import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import NotificationBell from './NotificationBell';
import OrganizationSwitcher from './OrganizationSwitcher';
import ViewAsBanner from './ViewAsBanner';
import Icons from './Icons';

const VolunteerNav = ({ currentPage }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Determine active page from URL or prop
  const getActivePage = () => {
    if (currentPage) return currentPage;
    const path = location.pathname;
    if (path.includes('/my-tasks')) return 'My Tasks';
    if (path.includes('/calendar')) return 'Calendar';
    if (path.includes('/documents')) return 'Documents';
    if (path.includes('/volunteers')) return 'Volunteers';
    if (path.includes('/notifications')) return 'Notifications';
    if (path.includes('/profile')) return 'Profile';
    return 'Dashboard';
  };

  const activePage = getActivePage();

  return (
    <>
      <ViewAsBanner />
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-white shadow-sm border-b z-20">
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-gray-600 hover:text-gray-900 flex-shrink-0"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div className="flex items-center gap-2 mx-2 min-w-0">
            {user?.organizationLogo && (
              <img
                src={`${process.env.REACT_APP_API_URL}/org-logo/${user.organizationId}`}
                alt="Organization logo"
                className="h-7 w-7 object-contain flex-shrink-0 rounded"
              />
            )}
            <h1 className="text-lg font-bold text-gray-900 truncate">{user?.organizationName || 'Dashboard'}</h1>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <NotificationBell />
            <button
              onClick={logout}
              className="text-red-500 hover:text-red-700 p-1"
              title="Logout"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-30
        lg:static lg:inset-auto
        w-64 bg-white border-r border-gray-200
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        flex flex-col
        h-screen lg:h-auto
      `}>
        {/* Mobile: Top spacing for header */}
        <div className="h-16 lg:hidden flex-shrink-0"></div>

        {/* Organization Name - Desktop Only */}
        <div className="hidden lg:flex flex-shrink-0 border-b border-gray-200 p-4 items-center justify-between gap-2">
          <div className="min-w-0 flex-1 flex items-center gap-3">
            {user?.organizationLogo && (
              <img
                src={`${process.env.REACT_APP_API_URL}/org-logo/${user.organizationId}`}
                alt="Organization logo"
                className="h-10 w-10 object-contain flex-shrink-0 rounded"
              />
            )}
            <div className="min-w-0">
              <h1 className="text-lg font-semibold text-gray-900 truncate">
                {user?.organizationName || 'Volunteer Portal'}
              </h1>
              <p className="text-xs text-gray-500 mt-1">{activePage}</p>
            </div>
          </div>
          <div className="flex-shrink-0">
            <NotificationBell />
          </div>
        </div>

        {/* Organization Switcher (only shows if volunteer is in multiple orgs) */}
        <OrganizationSwitcher />

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
          {[
            { label: 'Dashboard', to: '/dashboard', icon: Icons.chart },
            { label: 'My Tasks', to: '/my-tasks', icon: Icons.clipboard },
            { label: 'Calendar', to: '/calendar', icon: Icons.calendar },
            { label: 'Documents', to: '/documents', icon: Icons.doc },
            { label: 'Volunteers', to: '/volunteers', icon: Icons.users },
            { label: 'Notifications', to: '/notifications', icon: Icons.bell },
            { label: 'Profile', to: '/profile', icon: Icons.user },
          ].map((item) => (
            <Link
              key={item.label}
              to={item.to}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center space-x-3 w-full px-4 py-3 rounded-lg font-medium text-sm transition-colors ${
                activePage === item.label
                  ? 'bg-blue-50 text-blue-600'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <span className={activePage === item.label ? 'text-blue-600' : 'text-gray-400'}>
                {item.icon("w-4 h-4")}
              </span>
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>

        {/* User Info & Logout */}
        <div className="flex-shrink-0 border-t border-gray-200 p-4 pb-8 lg:pb-4 bg-white">
          <div className="mb-3">
            <p className="text-sm font-medium text-gray-900">
              {user?.firstName || 'Volunteer'} {user?.lastName || ''}
            </p>
            <p className="text-xs text-gray-500">{user?.email}</p>
          </div>
          <button
            onClick={logout}
            className="w-full bg-danger-500 hover:bg-danger-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
          >
            Logout
          </button>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-20"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}
    </>
  );
};

export default VolunteerNav;
